import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import SystemParameter, {
  ISystemParameter
} from "../../model/systemparameter/SystemParameter";
import Application from "../../Application";
import GetSystemParametersInteractor from "../../interactor/systemparameter/GetSystemParametersInteractor";
import RemoveSystemParametersInteractor from "../../interactor/systemparameter/RemoveSystemParametersInteractor";
import CreateSystemParameterInteractor from "../../interactor/systemparameter/CreateSystemParameterInteractor";
import UpdateSystemParameterInteractor from "../../interactor/systemparameter/UpdateSystemParameterInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import SystemParameterPresentationModel, {
  TSystemParameterPresentationModel
} from "./model/SystemParameterPresentationModel";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";

export interface TSystemParameterPresenter extends TLoadingAwarePresenter {
  systemParameters: ISystemParameter[];
}

export interface ISystemParameterPresenter
  extends TSystemParameterPresenter,
    TPresentable {
  getSystemParameters(): void;
  deleteSystemParameter(id: number): void;
  setSystemParameters(systemParameter: ISystemParameter[]): void;
  createSystemParameter(data: TSystemParameterPresentationModel): void;
  updateSystemParameter(data: TSystemParameterPresentationModel): void;
}

const defaultState: TSystemParameterPresenter = {
  systemParameters: []
};

function formatSystemParameter(data: any) {
  return SystemParameter(data);
}

const SystemParameterPresenter = withStore<
  ISystemParameterPresenter,
  TSystemParameterPresenter
>(({ application, store, loader, translate }): ISystemParameterPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TSystemParameterPresenter>();

  loader.start("systemParametersLoader");

  const setSystemParameters = (systemParameters: ISystemParameter[]) => {
    _store.update({
      systemParameters: systemParameters.map((systemParameter) => {
        return SystemParameterPresentationModel(systemParameter);
      })
    });
    loader.stop("systemParametersLoader");
  };
  const getSystemParameters = async () => {
    try {
      let systemParameters = await _application.container
        .resolve<GetSystemParametersInteractor>("getSystemParameters")
        .execute();
      setSystemParameters(systemParameters);
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const deleteSystemParameter = async (id: number) => {
    try {
      await _application.container
        .resolve<RemoveSystemParametersInteractor>("removeSystemParameters")
        .execute(id);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "system_parameters_page.messages.delete_system_parameter_success"
          )
        );
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
    await getSystemParameters();
  };

  const createSystemParameter = async (
    data: TSystemParameterPresentationModel
  ) => {
    try {
      const systemParameter = formatSystemParameter(data);
      await _application.container
        .resolve<CreateSystemParameterInteractor>("createSystemParameter")
        .execute(systemParameter);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "system_parameters_page.messages.create_system_parameter_success"
          )
        );
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
    await getSystemParameters();
  };

  const updateSystemParameter = async (
    data: TSystemParameterPresentationModel
  ) => {
    try {
      const systemParameter = formatSystemParameter(data);
      await _application.container
        .resolve<UpdateSystemParameterInteractor>("updateSystemParameter")
        .execute(systemParameter);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "system_parameters_page.messages.update_system_parameter_success"
          )
        );
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
    await getSystemParameters();
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    setSystemParameters,
    getSystemParameters,
    deleteSystemParameter,
    createSystemParameter,
    updateSystemParameter
  };
}, defaultState);

export default SystemParameterPresenter;
